import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Image } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./CustomDragDrop.css";
import CustomButton from "./CustomButton";

const CustomDragDrop = ({
  onUplodfiles,
  nameKey = "",
  allowMultiple = true,
  existingFiles = [], // ✅ for edit mode
}) => {
  const [tempFiles, setTempFiles] = useState([]);

  // ✅ Preload existing URLs when editing
  useEffect(() => {
    if (existingFiles && existingFiles.length > 0) {
      const formatted = existingFiles.map((url) => ({
        type: getFileTypeFromUrl(url),
        data: url,
        name: url.split("/").pop(),
        isExisting: true,
      }));
      setTempFiles(formatted);
    }
  }, [existingFiles]);

  // ✅ Determine file type
  const getFileTypeFromUrl = (url) => {
    if (/\.(jpeg|jpg|png|gif|webp|avif|svg)$/i.test(url)) return "image";
    if (/\.(pdf)$/i.test(url)) return "pdf";
    if (/\.(doc|docx)$/i.test(url)) return "word";
    if (/\.(xls|xlsx|csv)$/i.test(url)) return "excel";
    return "file";
  };

  // ✅ Handle file drop
  const onDrop = useCallback(
    (acceptedFiles) => {
      const filesToProcess = allowMultiple ? acceptedFiles : acceptedFiles.slice(0, 1);

      filesToProcess.forEach((file) => {
        const isImage = file.type.startsWith("image/");
        if (isImage) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = () => {
            setTempFiles((prevItems) =>
              allowMultiple
                ? [...prevItems, { type: "image", data: reader.result, name: file.name }]
                : [{ type: "image", data: reader.result, name: file.name }]
            );
          };
        } else {
          const fileType = getFileTypeFromUrl(file.name);
          setTempFiles((prevItems) =>
            allowMultiple
              ? [...prevItems, { type: fileType, data: null, name: file.name }]
              : [{ type: fileType, data: null, name: file.name }]
          );
        }
      });

      onUplodfiles(filesToProcess, nameKey);
    },
    [allowMultiple, nameKey, onUplodfiles]
  );

  const { open, getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: allowMultiple,
  });

  const handleRemove = (index) => {
    setTempFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ✅ Render Bootstrap file icons
  const renderFileIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return <i className="bi bi-file-earmark-pdf text-danger fs-3"></i>;
      case "word":
        return <i className="bi bi-file-earmark-word text-primary fs-3"></i>;
      case "excel":
        return <i className="bi bi-file-earmark-excel text-success fs-3"></i>;
      default:
        return <i className="bi bi-file-earmark text-secondary fs-3"></i>;
    }
  };

  return (
    <section className="container drag-drop-section mb-4">
      {tempFiles?.length > 0 && (
        <div className="uploded-file-div">
          <h2>{"Files"}</h2>
          <div className="uploded-images-div">
            {tempFiles.map((file, index) => (
              <div className="file-container" key={index}>
                {file.type === "image" ? (
                  <div className="img-container">
                    <Image src={file.data} alt={file.name} />
                  </div>
                ) : (
                  <div className="file-preview-container text-center">
                    {renderFileIcon(file.type)}
                    <p className="file-name">{file.name}</p>
                    {file.data && (
                      <a href={file.data} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    )}
                  </div>
                )}
                <button type="button" className="remove-btn" onClick={() => handleRemove(index)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div {...getRootProps({ className: "dropzone drag-drop" })}>
        <input {...getInputProps()} />
        <h6>
          {allowMultiple ? "Drag 'n' drop multiple files here," : "Drag 'n' drop a file here,"}{" "}
          <br className="mobile" />
          {" or click to select file(s)"}
        </h6>
        <div className="action-buttons">
          <CustomButton buttonClasses={"submit-btn drag-drop-btn"} type="button" onClick={open}>
            {allowMultiple ? "Upload Files" : "Upload File"}
          </CustomButton>
        </div>
      </div>
    </section>
  );
};

export default CustomDragDrop;
